import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { RetractableAwningForm } from "@/components/RetractableAwningForm";
import { RetractableInstallGallery } from "@/components/RetractableInstallGallery";
import { RetractableStickyCta } from "@/components/RetractableStickyCta";
import { SeriesBadgesSection } from "@/components/SeriesBadgesSection";
import { SolairDealerBar } from "@/components/SolairDealerBar";
import { Link } from "@tanstack/react-router";
import { PergolaUpgradeCta } from "@/components/PergolaUpgradeCta";
import heroAsset from "@/assets/solair-awning-red-pillows-deck.jpg.asset.json";
import fallLeavesAsset from "@/assets/solair-awning-fall-leaves-deck.jpg.asset.json";
import coffeeAsset from "@/assets/solair-awning-morning-coffee-porch.jpg.asset.json";
import backyardAsset from "@/assets/solair-awning-backyard-view.jpg.asset.json";
import heroBannerAsset from "@/assets/retractable-awning-deck-gathering-san-antonio.jpg.asset.json";

const hero = heroAsset.url;
const patio = fallLeavesAsset.url;
const cassette = coffeeAsset.url;
const remote = backyardAsset.url;
const heroBanner = heroBannerAsset.url;


export const Route = createFileRoute("/retractable-awnings")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Retractable Awnings for Patios & Decks — Motorized in Texas | Texas Made Shade",
      description: "Custom motorized retractable patio & deck awnings — push-button shade for Texas decks, patios and outdoor rooms. Local awning company near San Antonio & Austin.",
      image: hero,
      imageAlt: "Motorized retractable awning extended over a Texas Hill Country patio at golden hour",
      socialTitle: "Motorized Retractable Awnings — Texas Made Shade",
      socialDescription:
        "Push-button shade for your patio or deck. Custom motorized retractable awnings designed, fabricated and installed across Central & South Texas.",
      keywords:
        "retractable awnings San Antonio, retractable awnings Austin TX, motorized retractable awnings Texas, retractable patio awning, patio awning, patio awnings, deck awning, awnings for decks, retractable awning for deck, awning for patio, awning for deck, awnings near me, motorized patio shades Austin, motorized shades San Antonio, motorized shades Austin TX, automatic patio shade, smart shades Austin TX, motorized outdoor shades with side tracks, lateral arm awning, Sunbrella retractable awning, electric patio awning, Somfy motor awning Texas, Solair® dealer San Antonio, Solair® dealer Austin, awning company San Antonio, Austin awning company",
    }),
    scripts: serviceHeadScripts({
      path: "/retractable-awnings",
      serviceName: "Retractable Awnings",
      breadcrumbName: "Retractable Awnings",
      description:
        "Motorized retractable patio and deck awnings — premium Sunbrella canvas, brushed-aluminum lateral-arm cassettes, Somfy-style remote and wind/sun sensors.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/retractable-awnings"
      serviceName="Retractable Awnings"
      hideLetsTalkForm

      keywordOutlineImageSrc={hero}
      keywordOutlineImageAlt="Motorized retractable awning extended over a Texas Hill Country patio at golden hour"
      keywordOutline={[
        { id: "overview", label: "Motorized Retractable Awnings in Texas" },
        { id: "why-us", label: "Why Texas Made Shade for Retractable Awnings" },
        { id: "details", label: "Cassette, Arms, Motor & Canvas Details" },
        { id: "applications", label: "Where Retractable Awnings Work Best" },
        { id: "faqs", label: "Retractable Awning FAQs" },
      ]}
      eyebrow="★ RETRACTABLE AWNINGS ★"
      title="Motorized Retractable Awnings — Push-Button Shade for Your Patio or Deck"
      intro="Custom motorized retractable patio and deck awnings designed, fabricated and installed by Texas Made Shade. Lateral-arm cassettes in brushed aluminum, premium Sunbrella canvas, Somfy-style motors with handheld remote and optional wind/sun sensors — sized to your home, your sun exposure and your HOA. Built in our Seguin, TX shop and installed across San Antonio, Austin, New Braunfels and the Hill Country."
      heroImage={heroBanner}
      heroImageClassName="object-cover object-center opacity-100"
      preContent={
        <>
          <section aria-labelledby="solair-dealer" className="border-y-2 border-navy-deep/10 bg-sand/60">
            <div className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
              <SolairDealerBar variant="light" />
              <h2 id="solair-dealer" className="mt-6 font-display text-2xl font-bold uppercase tracking-wide text-navy-deep md:text-3xl">
                Authorized Solair® Dealer for Retractable Awnings
              </h2>
              <p className="mt-3 max-w-3xl text-base text-navy-deep/80">
                Our motorized retractable awnings are built around genuine Solair® Shade Solutions hardware — heavy extruded
                cassettes, lateral arms, Somfy® motors and Sunbrella® canvas — ordered factory-direct and installed by our own
                Seguin, TX crew. Because we&rsquo;re an authorized dealer, replacement motors, brackets, end caps and matching fabric
                are still available years after your install.
              </p>
              <div className="mt-5 flex flex-wrap gap-4 text-sm font-semibold uppercase tracking-[0.16em]">
                <Link to="/exterior-vertical-shades" className="text-red underline underline-offset-4">
                  Solair® motorized drop screens
                </Link>
                <Link to="/solair-vs" className="text-navy-deep underline underline-offset-4">
                  Compare Solair® systems
                </Link>
              </div>
            </div>
          </section>
          <SeriesBadgesSection />
        </>
      }
      features={[
        "Motorized push-button extension and retraction",
        "Premium Sunbrella® canvas — solids and stripes",
        "Brushed-aluminum lateral-arm cassette housing",
        "Optional wind & sun sensors for hands-free operation",
        "Wall, soffit or fascia mounting for any home",
        "Designed, sewn and installed by our own crew",
      ]}
      body={[
        {
          heading: "It expands your living space.",
          text: "A retractable awning turns a sun-baked patio into a cool, comfortable outdoor room you'll actually use — for entertaining, family dinners, or just a quiet drink at the end of the day. It's like adding another room onto your house, only it folds away when you don't want it.",
        },
        {
          heading: "It's versatile — covered or uncovered, your choice.",
          text: "Want full sun this morning and shade by 3 p.m.? With one push of a button the awning extends in under a minute, and tucks back into its cassette just as easily. No poles to drag out, no canopy to take down before a storm.",
        },
        {
          heading: "It looks fabulous.",
          text: "Extended, the canvas brings a sharp, custom-tailored look to your patio in stylish Sunbrella® fabric and a graceful aluminum frame. Retracted, the slim cassette tucks against the wall and disappears into your home's architecture — no bulky hardware on display.",
        },
        {
          heading: "It blocks heat where it matters.",
          text: "Sunbrella canvas blocks UV and dramatically cuts solar heat gain on south- and west-facing walls — keeping your patio (and the rooms behind those big sliding doors) noticeably cooler in Texas summers, and easier to cool with the AC.",
        },
        {
          heading: "Looking for a fixed welded frame instead?",
          text: "If you want a permanent shade structure that never needs to retract, we also build welded aluminum patio canopies, gazebo-pergola covers and storefront awnings.",
          cta: { label: "See our welded aluminum awnings", href: "/awnings" },
        },
      ]}
      applications={[
        "Backyard patios",
        "Decks & balconies",
        "Outdoor kitchens",
        "Pool decks",
        "South & west-facing walls",
        "Restaurant patios",
      ]}
      faqs={[
        {
          q: "How wide can a retractable awning be in Texas?",
          a: "We commonly build motorized retractable awnings from about 8 ft up to 24 ft wide, with projections from 5 ft to 13 ft. Anything wider than ~24 ft is typically built as two awnings side by side so you keep the slim, single-cassette look without overstressing the arms.",
        },
        {
          q: "Will a retractable awning hold up to Texas wind and storms?",
          a: "Lateral-arm retractable awnings are not designed to ride out high wind — by design, you retract them. We strongly recommend our optional wind/sun sensor that automatically retracts the awning when gusts hit the threshold you set, even when you're not home. For year-round, no-touch shade in windy locations we'll steer you toward a fixed welded aluminum patio canopy instead.",
        },
        {
          q: "What fabric do retractable awnings come in?",
          a: "We sew our retractable awning canvases from premium Sunbrella® acrylic — the same marine-grade, UV-stable, mildew-resistant fabric we use on our welded storefront awnings. You pick from solids, stripes and tweeds; we'll bring swatches to your free on-site measurement.",
        },
        {
          q: "Can I operate it from my phone or with Alexa / Google Home?",
          a: "Yes. We use Somfy-style 120V motors with a handheld RTS remote standard, and we can add a smart-home hub so you can extend or retract the awning from your phone, Alexa, Google Home or Apple Home, and put it on a daily schedule.",
        },
        {
          q: "Do you install retractable awnings in San Antonio, Austin and the Hill Country?",
          a: "Yes — we're based in Seguin and install motorized retractable awnings across San Antonio, Austin, New Braunfels, San Marcos, Boerne, Kerrville, Fredericksburg and the rest of Central & South Texas.",
        },
        {
          q: "How do I get a free retractable awning quote?",
          a: "Send a few photos of the wall and patio plus rough width measurements through the form on this page, or call +1 (830) 390-9588. On-site measurements are free across our Texas service area.",
        },
        {
          q: "What motor options are available?",
          a: "Every awning ships standard with a quiet 11V-120V Somfy-style tubular motor and a handheld 5-channel RTS remote. Optional upgrades include a hardwired wall switch, a wind/sun automation sensor that retracts the awning automatically, and a smart-home hub that lets you operate the awning from your phone, Alexa, Google Home or Apple Home — and put it on a daily schedule. We can also build a manual hand-crank version on smaller widths if you prefer no electrical work.",
        },
        {
          q: "What drives the price of a retractable awning?",
          a: "Four things: (1) width and projection — bigger awnings need heavier arms and more canvas; (2) Sunbrella® fabric choice — solids are the most affordable, premium tweeds and woven stripes cost more; (3) motor and automation — a basic remote setup is included, while wind/sun sensors and smart-home control add a small upgrade; (4) mounting and electrical — wall mounts on stucco or wood are straightforward, while soffit/fascia brackets, brick, or running new 120V power adds labor. Most residential motorized retractables in Texas land in the mid-four-figure range installed; we send a written quote with line-item options so you can pick what matters to you.",
        },
        {
          q: "What areas do you install retractable awnings in?",
          a: "We're based in Seguin, TX and install across the entire I-35 corridor and the Hill Country — San Antonio, New Braunfels, Austin, San Marcos, Schertz, Cibolo, Boerne, Kerrville, Fredericksburg, and out toward the Gulf Coast (Victoria, Cuero, Gonzales). Free on-site measurements anywhere in our Texas service area. If you're outside that radius, send photos and we'll let you know if we can still build and ship for self-install.",
        },
        {
          q: "What kind of warranty comes with a Texas Made Shade retractable awning?",
          a: "Welded aluminum cassette and lateral-arm frames carry a 10-year structural warranty. Sunbrella® canvas carries the manufacturer's 10-year limited warranty against fading, mildew and UV breakdown. Somfy-style motors carry a 5-year manufacturer warranty. Our installation workmanship is warranted for 1 year. We service everything we install — if anything ever needs adjustment or a part replaced, you call us, not a 1-800 number.",
        },
      ]}
      gallerySection={{
        eyebrow: "Solair® Retractable Awnings · Installed Across Texas",
        title: "Solair® Retractable Awning Patio & Deck Installs",
        intro:
          "Real Solair® lateral-arm retractable awnings shading decks, porches and backyard living rooms. Every awning is sized, fabricated and installed by the same Texas Made Shade crew.",
        categories: ["Decks", "Porches", "Backyards"],
        images: [
          {
            src: hero,
            alt: "Solair® motorized retractable awning fully extended over a raised white-railing deck with lounge seating",
            label: "Raised Deck · Cream Canvas",
            category: "Decks",
          },
          {
            src: patio,
            alt: "Solair® retractable awning shading a deck seating area with fall foliage in the background",
            label: "Fall Deck Retreat",
            category: "Decks",
          },
          {
            src: cassette,
            alt: "Couple enjoying morning coffee on a shaded porch under a Solair® retractable awning",
            label: "Morning Coffee Porch",
            category: "Porches",
          },
          {
            src: remote,
            alt: "View from beneath an extended Solair® retractable awning looking out over a lush green backyard",
            label: "Backyard Living Room",
            category: "Backyards",
          },
        ],
        columns: 4,

        cta: {
          headline: "Get a Free Estimate on Your Retractable Awning",
          subhead:
            "Backyard patio, deck or restaurant — send a few photos and rough wall measurements and we'll come back same business day with sizing, fabric and motor options. Built in Seguin, installed across Texas.",
          buttonLabel: "Get My Free Estimate",
          href: "#retractable-awning-quote",
          phoneNumber: "+1 (830) 390-9588",
        },
      }}
      testimonials={{
        eyebrow: "Awning Customer Reviews · 5.0 Rating",
        title: "What Patio Awning Customers Say",
        items: [
          {
            quote: "Push a button and we have shade in under a minute. Best upgrade we've made to the back patio.",
            author: "Allison R.",
            location: "San Antonio, TX",
            role: "Retractable Patio Awning",
            rating: 5,
          },
          {
            quote: "The wind sensor automatically retracts it before storms. We don't have to think about it. Beautifully built.",
            author: "Mark D.",
            location: "Austin, TX",
            role: "Motorized Awning",
            rating: 5,
          },
          {
            quote: "Sunbrella canvas in a custom stripe to match the house — the install crew was meticulous.",
            author: "Janelle B.",
            location: "New Braunfels, TX",
            role: "Retractable Deck Awning",
            rating: 5,
          },
          {
            quote: "Cooler patio, cooler house. Our west-facing living room is noticeably easier to cool now.",
            author: "Tom W.",
            location: "Boerne, TX",
            role: "South/West Wall Awning",
            rating: 5,
          },
        ],
      }}
      postCta={
        <>
        <section className="border-y-4 border-navy-deep bg-navy-deep py-14 text-white sm:py-20">
          <div className="mx-auto max-w-5xl px-4 text-center lg:px-8">
            <div className="font-display text-sm tracking-[0.3em] text-white/70">
              ★ READY FOR PUSH-BUTTON SHADE?
            </div>
            <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl lg:text-5xl">
              Get a Free Estimate on Your Retractable Awning
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-white/80 sm:text-lg">
              Send a few photos of your wall and patio plus rough width and projection — we'll reply
              same business day with sizing, fabric and motor options, then a written quote. Built in
              Seguin, installed across Texas.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a
                href="#retractable-awning-quote"
                data-cta-location="post_cta"
                className="inline-flex items-center justify-center gap-2 bg-white px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-navy-deep transition hover:bg-cream"
              >
                Get My Free Estimate
              </a>
              <a
                href="tel:+18303909588"
                className="inline-flex items-center justify-center gap-2 border-2 border-white px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-white hover:text-navy-deep"
              >
                Call +1 (830) 390-9588
              </a>
            </div>
            <p className="mt-6 text-xs uppercase tracking-wider text-white/60">
              Free on-site measurements · San Antonio · Austin · New Braunfels · Hill Country
            </p>
          </div>
        </section>
        <PergolaUpgradeCta />
        </>
      }
    >
      <RetractableAwningForm />
      <RetractableInstallGallery />
      <RetractableStickyCta />
    </ServicePage>
  ),
});
